import { Box, InputAdornment, ListSubheader, MenuItem, SelectChangeEvent, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { FormSelect } from "component/FormSelect";
import { LayerIcon } from "component/Icons";
import { UNKNOWN } from "constant";
import orderBy from "lodash/orderBy";
import { AssetIdentifier, AssetStatus } from "module/common";
import { useMemo } from "react";
import { COLORS } from "style";
import { transformAssetView } from "utility/transformer";
import * as api from "api";
import { CalculatedRecommendation, RecommendationWithRelated } from "type/model/api";
import { AssetView, AssetViewStatus } from "type/model/view";
import { LoadingIndicator } from "component/LoadingIndicator";
import { ErrorAlert } from "component/ErrorAlert";

interface Props {
  label: string;
  value: string | number | null | undefined;
  onChange?: (event: SelectChangeEvent<unknown>, child: React.ReactNode) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error?: string | undefined;
  changeAssetInfo?: (id: number, asset_name: string | null | undefined, callSign: string | null | undefined) => void;
  recommendations?: RecommendationWithRelated[];
  calculatedRecommendations?: CalculatedRecommendation[];
}

const FIGURES: { label: string; key: keyof CalculatedRecommendation }[] = [
  { label: "Time to patient", key: "time_to_patient" },
  { label: "Time out of hospital", key: "time_out_of_hospital" },
  { label: "Total medical time", key: "total_medical_time" },
  { label: "Total mission time", key: "total_mission_time" },
];

const COLUMNS = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 3fr) repeat(4, minmax(0, 1fr)) minmax(0, 2fr)",
  columnGap: 2,
  alignItems: "center",
};

const FiguresHeader = () => (
  <ListSubheader sx={{ ...COLUMNS, bgcolor: COLORS.DR_WHITE }}>
    <span />
    {FIGURES.map(({ label, key }) => (
      <Typography key={key} variant="body2" color={COLORS.DARK_SOULS}>
        {label}
      </Typography>
    ))}
    <span />
  </ListSubheader>
);

const Figure = ({ minutes }: { minutes: number }) => (
  <Typography variant="body2">
    {Math.floor(minutes / 60)}hr {minutes % 60}min
  </Typography>
);

export const AssetSelect = (props: Props) => {
  const { recommendations, calculatedRecommendations } = props;
  const assetsQuery = useQuery({ queryKey: ["assets"], queryFn: api.fetchAssets });
  const { assets, figuresById } = useMemo(() => {
    const recommendedById = new Map(
      (recommendations ?? []).map((recommendation) => [recommendation.asset_id, recommendation])
    );
    const calculatedOrder = new Map(
      (calculatedRecommendations ?? []).map((recommendation, index) => [recommendation.asset_id, index])
    );
    const figuresById = new Map<number | undefined, CalculatedRecommendation>([
      ...(calculatedRecommendations ?? []).map((recommendation) => [recommendation.asset_id, recommendation] as const),
      ...(recommendations ?? []).map((recommendation) => [recommendation.asset_id, recommendation] as const),
    ]);
    const all = (assetsQuery.data?.data ?? []).map(transformAssetView);
    const dashboardOrder = (asset: AssetView) =>
      `${asset.status === AssetViewStatus.Online ? 0 : 1}  ${asset.dashboardOrder.toString(10).padStart(6)}`;

    if (!recommendations) {
      return { assets: orderBy(all, dashboardOrder), figuresById };
    }

    const recommended = orderBy(
      all.filter((asset) => recommendedById.has(asset.id)),
      (asset) => recommendedById.get(asset.id)?.position ?? Infinity
    );
    const calculated = orderBy(
      all.filter((asset) => !recommendedById.has(asset.id) && calculatedOrder.has(asset.id)),
      (asset) => calculatedOrder.get(asset.id)
    );
    const rest = orderBy(
      all.filter((asset) => !recommendedById.has(asset.id) && !calculatedOrder.has(asset.id)),
      dashboardOrder
    );

    return { assets: [...recommended, ...calculated, ...rest], figuresById };
  }, [assetsQuery.data, recommendations, calculatedRecommendations]);

  const showFigures = figuresById.size > 0;

  const getAssetName = (assetId: unknown) => {
    if (!assetId) {
      return (
        <Typography variant="body2" color={COLORS.DARK_SOULS}>
          Select an asset for this mission
        </Typography>
      );
    }
    const result = assets.find((asset) => asset.id === assetId);
    return result?.name ?? "";
  };

  if (assetsQuery.isPending) {
    return <LoadingIndicator size={20} />;
  }

  if (assetsQuery.isError) {
    return <ErrorAlert>Something went wrong!</ErrorAlert>;
  }

  const {
    changeAssetInfo,
    onChange,
    recommendations: _recommendations,
    calculatedRecommendations: _calculatedRecommendations,
    ...otherProps
  } = props;

  const assetItems = assets.map((asset) => {
    const figures = figuresById.get(asset.id);

    return {
      value: asset.id.toString(),
      element: (
        <MenuItem
          key={asset.id}
          value={asset.id}
          sx={showFigures ? COLUMNS : { justifyContent: "space-between" }}
          autoFocus={false}
        >
          <AssetIdentifier asset={asset} />
          {showFigures
            ? FIGURES.map(({ key }) =>
                figures ? <Figure key={key} minutes={(figures[key] as number | undefined) ?? 0} /> : <span key={key} />
              )
            : null}
          <Box sx={showFigures ? { justifySelf: "end", maxWidth: "100%", whiteSpace: "normal" } : undefined}>
            <AssetStatus asset={asset} />
          </Box>
        </MenuItem>
      ),
      readOnlyText: asset.name || UNKNOWN,
    };
  });

  const headerItem = { value: "", element: <FiguresHeader key="figures-header" />, readOnlyText: "" };

  return (
    <FormSelect
      {...otherProps}
      required
      fullWidth
      displayEmpty={true}
      renderValue={getAssetName}
      onChange={(e, child) => {
        onChange && onChange(e, child);
      }}
      startAdornment={
        <InputAdornment position="start">
          <LayerIcon />
        </InputAdornment>
      }
      items={showFigures ? [headerItem, ...assetItems] : assetItems}
    />
  );
};
